import { supabase } from "@/integrations/supabase/client";
import { formatPersona, formatPersonaBrief, type Persona, type PersonaBrief, type PersonaFormat } from "@/utils/personaFormatters";

export interface ShortcodeOptions {
  format?: PersonaFormat;
  attribute?: string;
}

export class ShortcodeProcessor {
  private personaCache = new Map<string, Persona>();
  private briefCache = new Map<string, PersonaBrief>();
  private assetCache = new Map<string, any>();
  private brandCache = new Map<string, any>();
  private collabTypeCache = new Map<string, any>();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps = new Map<string, number>();

  async processShortcodes(text: string, brandId: string | null): Promise<string> {
    if (!brandId) return text;

    let processedText = text;

    // Process persona shortcodes: {persona:name} or {persona:name.attribute} or {persona:name:format}
    const personaRegex = /\{persona:([^}]+)\}/g;
    const personaMatches = Array.from(text.matchAll(personaRegex));
    
    for (const match of personaMatches) {
      const [fullMatch, params] = match;
      const { name, attribute, format } = this.parseShortcodeParams(params);
      
      try {
        const persona = await this.getPersona(name, brandId);
        if (persona) {
          const replacement = this.formatPersonaReplacement(persona, attribute, format);
          processedText = processedText.replace(fullMatch, replacement);
        } else {
          processedText = processedText.replace(fullMatch, `[Persona "${name}" not found]`);
        }
      } catch (error) {
        console.error(`Error processing persona shortcode ${fullMatch}:`, error);
        processedText = processedText.replace(fullMatch, `[Error loading persona "${name}"]`);
      }
    }

    // Process persona brief shortcodes: {personabrief:name} or {personabrief:name:format}
    const briefRegex = /\{personabrief:([^}]+)\}/g;
    const briefMatches = Array.from(text.matchAll(briefRegex));
    
    for (const match of briefMatches) {
      const [fullMatch, params] = match;
      const { name, format } = this.parseShortcodeParams(params);
      
      try {
        const brief = await this.getPersonaBrief(name, brandId);
        if (brief) {
          const replacement = formatPersonaBrief(brief, format);
          processedText = processedText.replace(fullMatch, replacement);
        } else {
          processedText = processedText.replace(fullMatch, `[Persona Brief "${name}" not found]`);
        }
      } catch (error) {
        console.error(`Error processing persona brief shortcode ${fullMatch}:`, error);
        processedText = processedText.replace(fullMatch, `[Error loading persona brief "${name}"]`);
      }
    }

    // Process brand asset shortcodes: {asset:name}
    const assetRegex = /\{asset:([^}]+)\}/g;
    const assetMatches = Array.from(text.matchAll(assetRegex));
    
    for (const match of assetMatches) {
      const [fullMatch, assetName] = match;
      
      try {
        const asset = await this.getBrandAsset(assetName, brandId);
        if (asset) {
          const replacement = asset.color_value || asset.file_url || asset.description || asset.name;
          processedText = processedText.replace(fullMatch, replacement);
        } else {
          processedText = processedText.replace(fullMatch, `[Asset "${assetName}" not found]`);
        }
      } catch (error) {
        console.error(`Error processing asset shortcode ${fullMatch}:`, error);
        processedText = processedText.replace(fullMatch, `[Error loading asset "${assetName}"]`);
      }
    }

    // Process collaboration type shortcodes: {collab_type:list} or {collab_type:identifier:field}
    const collabTypeRegex = /\{collab_type:([^}]+)\}/g;
    const collabTypeMatches = Array.from(text.matchAll(collabTypeRegex));
    
    for (const match of collabTypeMatches) {
      const [fullMatch, params] = match;
      
      try {
        if (params === 'list') {
          // Return list of all collaboration types
          const types = await this.getCollaborationTypes(brandId);
          if (types && types.length > 0) {
            const formatted = types.map(t => 
              `${t.name}${t.description ? `: ${t.description}` : ''}`
            ).join('\n');
            processedText = processedText.replace(fullMatch, formatted);
          } else {
            processedText = processedText.replace(fullMatch, '[No collaboration types found]');
          }
        } else {
          // Parse identifier and field (field is required)
          const parts = params.split(':');
          const identifier = parts[0];
          const field = parts[1];
          
          if (!field) {
            processedText = processedText.replace(fullMatch, `[Missing field - use :name or :description]`);
            continue;
          }
          
          // Special case: if identifier is "id", skip processing - let edge function handle it
          if (identifier.toLowerCase() === 'id') {
            // Leave the shortcode as-is for edge function to process
            continue;
          }
          
          // Check if identifier is a UUID (ID) or a name
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
          
          let type;
          if (isUUID) {
            type = await this.getCollaborationTypeById(identifier, brandId);
          } else {
            type = await this.getCollaborationType(identifier, brandId);
          }
          
          if (type) {
            let replacement = '';
            if (field === 'name') {
              replacement = type.name;
            } else if (field === 'description') {
              replacement = type.description || '';
            } else if (field === 'prerequisites') {
              replacement = type.prerequisites || '';
            } else {
              replacement = `[Field "${field}" not supported]`;
            }
            processedText = processedText.replace(fullMatch, replacement);
          } else {
            processedText = processedText.replace(fullMatch, `[Collaboration type "${identifier}" not found]`);
          }
        }
      } catch (error) {
        console.error(`Error processing collaboration type shortcode ${fullMatch}:`, error);
        processedText = processedText.replace(fullMatch, `[Error loading collaboration type]`);
      }
    }

    // Process brand shortcodes: {brand:field} and direct brand shortcodes
    const brandRegex = /\{(?:brand:|brand_name|description|website|competitors|prompts|personas|seed_keywords|targeted_countries|seed_hashtags|brand_hashtags|languages|youtube|instagram|facebook|tiktok|x|linkedin)([^}]*)\}/g;
    const brandMatches = Array.from(text.matchAll(brandRegex));
    
    for (const match of brandMatches) {
      const [fullMatch] = match;
      let field = fullMatch.slice(1, -1); // Remove { and }
      
      // Handle brand: prefix
      if (field.startsWith('brand:')) {
        field = field.substring(6);
      }
      
      try {
        const brandData = await this.getBrandData(brandId);
        let replacement = '';
        
        if (brandData) {
          switch (field) {
            case 'brand_name':
            case 'name':
              replacement = brandData.name || '';
              break;
            case 'description':
              replacement = brandData.description || '';
              break;
            case 'website':
              replacement = brandData.website || '';
              break;
            case 'competitors':
              replacement = Array.isArray(brandData.competitors) ? brandData.competitors.join(', ') : '';
              break;
            case 'prompts':
              replacement = Array.isArray(brandData.prompts) ? brandData.prompts.join(', ') : '';
              break;
            case 'personas':
              replacement = Array.isArray(brandData.personas) ? brandData.personas.join(', ') : '';
              break;
            case 'seed_keywords':
              replacement = Array.isArray(brandData.seed_keywords) ? brandData.seed_keywords.join(', ') : '';
              break;
            case 'targeted_countries':
              replacement = Array.isArray(brandData.targeted_countries) ? brandData.targeted_countries.map((c: any) => c.name || c).join(', ') : '';
              break;
            case 'seed_hashtags':
              replacement = Array.isArray(brandData.seed_hashtags) ? brandData.seed_hashtags.join(', ') : '';
              break;
            case 'brand_hashtags':
              replacement = Array.isArray(brandData.brand_hashtags) ? brandData.brand_hashtags.join(', ') : '';
              break;
            case 'youtube':
              replacement = brandData.youtube || '';
              break;
            case 'instagram':
              replacement = brandData.instagram || '';
              break;
            case 'facebook':
              replacement = brandData.facebook || '';
              break;
            case 'tiktok':
              replacement = brandData.tiktok || '';
              break;
            case 'x':
              replacement = brandData.x || '';
              break;
            case 'linkedin':
              replacement = brandData.linkedin || '';
              break;
            case 'languages':
              if (Array.isArray(brandData.languages)) {
                replacement = brandData.languages.map((lang: any) => 
                  typeof lang === 'object' && lang.name ? 
                    `${lang.name} (${lang.code})` : 
                    lang
                ).join(', ');
              } else {
                replacement = '';
              }
              break;
            case 'currency':
              replacement = brandData.currency || 'USD';
              break;
            default:
              replacement = brandData[field] || `[Brand field "${field}" not found]`;
          }
        } else {
          replacement = `[Brand data not found]`;
        }
        
        processedText = processedText.replace(fullMatch, replacement);
      } catch (error) {
        console.error(`Error processing brand shortcode ${fullMatch}:`, error);
        processedText = processedText.replace(fullMatch, `[Error loading brand field "${field}"]`);
      }
    }

    // Process content & SEO shortcodes
    const contentRegex = /\{(content|structure|keywords)\}/g;
    const contentMatches = Array.from(text.matchAll(contentRegex));
    
    for (const match of contentMatches) {
      const [fullMatch, field] = match;
      
      // These would need to be provided as context or from specific content sources
      // For now, return placeholder text indicating what these shortcodes represent
      let replacement = '';
      switch (field) {
        case 'content':
          replacement = '[Original article/content text]';
          break;
        case 'structure':
          replacement = '[Content structure data]';
          break;
        case 'keywords':
          replacement = '[Keyword list]';
          break;
      }
      
      processedText = processedText.replace(fullMatch, replacement);
    }

    // Process utility shortcodes
    const utilityRegex = /\{(today|user:name)\}/g;
    const utilityMatches = Array.from(text.matchAll(utilityRegex));
    
    for (const match of utilityMatches) {
      const [fullMatch, utilityType] = match;
      
      let replacement = '';
      if (utilityType === 'today') {
        replacement = new Date().toLocaleDateString();
      } else if (utilityType === 'user:name') {
        replacement = 'User'; // Could be enhanced to get actual user name
      }
      
      processedText = processedText.replace(fullMatch, replacement);
    }

    // Process newsroom-specific shortcodes (these are placeholders for bulletin content)
    const newsroomRegex = /\{(articles|script|summary|headlines|title|general_title|bulletin_date|formatted_bulletin_date)\}/g;
    const newsroomMatches = Array.from(text.matchAll(newsroomRegex));
    
    for (const match of newsroomMatches) {
      const [fullMatch, field] = match;
      
      // These are placeholders that get replaced with actual bulletin content
      // When used in prompts, we show them as placeholder text
      let replacement = '';
      switch (field) {
        case 'articles':
          replacement = '[News articles content will be inserted here]';
          break;
        case 'script':
          replacement = '[Generated script will be inserted here]';
          break;
        case 'summary':
          replacement = '[Generated summary will be inserted here]';
          break;
        case 'headlines':
          replacement = '[Generated headlines will be inserted here]';
          break;
        case 'title':
          replacement = '[Bulletin title will be inserted here]';
          break;
        case 'general_title':
          replacement = '[General bulletin title will be inserted here]';
          break;
        case 'bulletin_date':
          replacement = '[Bulletin date will be inserted here]';
          break;
        case 'formatted_bulletin_date':
          replacement = '[Formatted bulletin date will be inserted here]';
          break;
      }
      
      processedText = processedText.replace(fullMatch, replacement);
    }

    return processedText;
  }

  // Process custom shortcodes: {{custom.key}}
  async processCustomShortcodes(text: string, customShortcodes: Record<string, string>): Promise<string> {
    if (!customShortcodes || Object.keys(customShortcodes).length === 0) return text;

    let processedText = text;
    const customRegex = /\{\{custom\.([^}]+)\}\}/g;
    const customMatches = Array.from(text.matchAll(customRegex));
    
    for (const match of customMatches) {
      const [fullMatch, key] = match;
      const value = customShortcodes[key];
      
      if (value !== undefined && value !== null) {
        processedText = processedText.replace(fullMatch, String(value));
      } else {
        processedText = processedText.replace(fullMatch, `[Custom "${key}" not found]`);
      }
    }

    return processedText;
  }

  private parseShortcodeParams(params: string): { name: string; attribute?: string; format: PersonaFormat } {
    const parts = params.split(':');
    const nameParts = parts[0].split('.');
    
    return {
      name: nameParts[0],
      attribute: nameParts[1],
      format: (parts[1] as PersonaFormat) || 'brief'
    };
  }

  private formatPersonaReplacement(persona: Persona, attribute?: string, format: PersonaFormat = 'brief'): string {
    if (attribute) {
      // Return specific attribute
      const value = persona[attribute as keyof Persona];
      return value ? String(value) : `[${attribute} not found]`;
    }
    
    return formatPersona(persona, format);
  }

  private async getPersona(name: string, brandId: string): Promise<Persona | null> {
    const cacheKey = `${brandId}:${name}`;
    const now = Date.now();
    
    // Check cache
    if (this.personaCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.personaCache.get(cacheKey) || null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('personas')
        .select('*')
        .eq('brand_id', brandId)
        .or(`name.ilike.%${name}%,persona_name.ilike.%${name}%`)
        .single();

      if (error) throw error;

      // Cache the result
      this.personaCache.set(cacheKey, data);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data;
    } catch (error) {
      console.error('Error fetching persona:', error);
      return null;
    }
  }

  private async getPersonaBrief(name: string, brandId: string): Promise<PersonaBrief | null> {
    const cacheKey = `brief:${brandId}:${name}`;
    const now = Date.now();
    
    // Check cache
    if (this.briefCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.briefCache.get(cacheKey) || null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('personas_briefs')
        .select('*')
        .eq('brand_id', brandId)
        .ilike('name', `%${name}%`)
        .single();

      if (error) throw error;

      // Cache the result
      this.briefCache.set(cacheKey, data);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data;
    } catch (error) {
      console.error('Error fetching persona brief:', error);
      return null;
    }
  }

  async getAvailablePersonas(brandId: string | null): Promise<Array<{ name: string; displayName: string }>> {
    if (!brandId) return [];

    try {
      const { data, error } = await supabase
        .from('personas')
        .select('name, persona_name')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;

      return data.map(p => ({
        name: p.name,
        displayName: p.persona_name || p.name
      }));
    } catch (error) {
      console.error('Error fetching available personas:', error);
      return [];
    }
  }

  async getAvailablePersonaBriefs(brandId: string | null): Promise<Array<{ name: string; displayName: string }>> {
    if (!brandId) return [];

    try {
      const { data, error } = await supabase
        .from('personas_briefs')
        .select('name')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;

      return data.map(b => ({
        name: b.name,
        displayName: b.name
      }));
    } catch (error) {
      console.error('Error fetching available persona briefs:', error);
      return [];
    }
  }

  private async getBrandAsset(variableName: string, brandId: string): Promise<any | null> {
    const cacheKey = `asset:${brandId}:${variableName}`;
    const now = Date.now();
    
    // Check cache
    if (this.assetCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.assetCache.get(cacheKey) || null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('brand_assets')
        .select('*')
        .eq('brand_id', brandId)
        .eq('variable_name', variableName)
        .single();

      if (error) throw error;

      // Cache the result
      this.assetCache.set(cacheKey, data);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data;
    } catch (error) {
      console.error('Error fetching brand asset:', error);
      return null;
    }
  }

  private async getBrandData(brandId: string): Promise<any | null> {
    const cacheKey = `brand:${brandId}`;
    const now = Date.now();
    
    // Check cache
    if (this.brandCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.brandCache.get(cacheKey) || null;
      }
    }

    try {
      // Get brand data from brands table and brand_data table
      const [brandResult, brandDataResult] = await Promise.all([
        supabase.from('brands').select('*').eq('id', brandId).single(),
        supabase.from('brand_data').select('*').eq('brand_id', brandId).single()
      ]);

      const combinedData = {
        ...brandResult.data,
        ...brandDataResult.data,
        name: brandResult.data?.name,
        website: brandDataResult.data?.website,
        description: brandDataResult.data?.description,
        currency: brandDataResult.data?.currency || 'USD'
      };

      // Cache the result
      this.brandCache.set(cacheKey, combinedData);
      this.cacheTimestamps.set(cacheKey, now);
      
      return combinedData;
    } catch (error) {
      console.error('Error fetching brand data:', error);
      return null;
    }
  }

  private async getCollaborationType(name: string, brandId: string): Promise<any | null> {
    const cacheKey = `collab_type:${brandId}:${name}`;
    const now = Date.now();
    
    // Check cache
    if (this.collabTypeCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.collabTypeCache.get(cacheKey) || null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('os_collabs_types')
        .select('*')
        .eq('brand_id', brandId)
        .eq('status', 'active')
        .ilike('name', name)
        .single();

      if (error) throw error;

      // Cache the result
      this.collabTypeCache.set(cacheKey, data);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data;
    } catch (error) {
      console.error('Error fetching collaboration type:', error);
      return null;
    }
  }

  private async getCollaborationTypeById(id: string, brandId: string): Promise<any | null> {
    const cacheKey = `collab_type_id:${brandId}:${id}`;
    const now = Date.now();
    
    // Check cache
    if (this.collabTypeCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.collabTypeCache.get(cacheKey) || null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('os_collabs_types')
        .select('*')
        .eq('id', id)
        .eq('brand_id', brandId)
        .single();

      if (error) throw error;

      // Cache the result
      this.collabTypeCache.set(cacheKey, data);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data;
    } catch (error) {
      console.error('Error fetching collaboration type by ID:', error);
      return null;
    }
  }

  private async getCollaborationTypes(brandId: string): Promise<any[]> {
    const cacheKey = `collab_types:${brandId}`;
    const now = Date.now();
    
    // Check cache
    if (this.collabTypeCache.has(cacheKey)) {
      const timestamp = this.cacheTimestamps.get(cacheKey) || 0;
      if (now - timestamp < this.cacheExpiry) {
        return this.collabTypeCache.get(cacheKey) || [];
      }
    }

    try {
      const { data, error } = await supabase
        .from('os_collabs_types')
        .select('*')
        .eq('brand_id', brandId)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;

      // Cache the result
      this.collabTypeCache.set(cacheKey, data || []);
      this.cacheTimestamps.set(cacheKey, now);
      
      return data || [];
    } catch (error) {
      console.error('Error fetching collaboration types:', error);
      return [];
    }
  }

  clearCache(): void {
    this.personaCache.clear();
    this.briefCache.clear();
    this.assetCache.clear();
    this.brandCache.clear();
    this.collabTypeCache.clear();
    this.cacheTimestamps.clear();
  }
}

// Singleton instance
export const shortcodeProcessor = new ShortcodeProcessor();